const defaults = {
    authWaitTime: 10000
};

let resolveFn: (() => void) | null = null;
const whenLoaded = new Promise<void>( resolve => {
    resolveFn = resolve;
});

export function initGravity( _config: {
    siteUrl: string;
    authWaitTime?: number;
}) {
    const config = { ...defaults, ... _config, siteUrl: new URL(_config.siteUrl).origin };
    return {
        embedHiddenSSIFrame() {
            let ssoFrame: HTMLIFrameElement | null = document.getElementById( 'ssIFrame_gravity' ) as HTMLIFrameElement | null;
            if ( ssoFrame ) {
                // already embedded.
                return;
            }
            const siteUrl = config.siteUrl;
            ssoFrame = document.createElement( 'iframe' );
            ssoFrame.id = 'ssIFrame_gravity';
            ssoFrame.src = `${siteUrl}/sso-iframe/`;
            // The bridge compares the referrer to the caller's origin.
            ssoFrame.referrerPolicy = 'origin';
            ssoFrame.style.width = '1px';
            ssoFrame.style.height = '1px';
            ssoFrame.style.position = 'absolute';
            ssoFrame.style.top = '-100px';
            ssoFrame.style.display = 'none';
            document.body.appendChild( ssoFrame );
            ssoFrame.addEventListener( 'load', () => {
                resolveFn?.();
            }, { once: true });
        },
        async proxyAuthenticate(): Promise<string|false> {
            await whenLoaded;
            
            let listener: any;
            const authPromise = new Promise<string|false>( resolve => {
                listener = this.getProxyListener( resolve );
                window.addEventListener( 'message', listener );
                this.sendMessageProxy( 'gravity::authenticate' );
            }).then(( token: string|false ) => {
                window.removeEventListener( 'message', listener );
                return token;
            });
            
            const authTimeout: Promise<false> = new Promise( resolve => {
                setTimeout(() => {
                    resolve( false );
                }, config.authWaitTime );
            });
            return Promise.race([ authPromise, authTimeout ]);
        },
        sendMessageProxy( message: string ) {
            const iFrame = document.getElementById( 'ssIFrame_gravity' ) as HTMLIFrameElement | null;
            if ( !iFrame || !iFrame.contentWindow ) {
                return;
            }
            iFrame.contentWindow.postMessage( message, config.siteUrl );
        },
        getProxyListener( resolve: (x: string|false) => void ) {
            return ( event: MessageEvent ) => {
                // Filter out React DevTools and other non-relevant messages
                if (event.origin !== config.siteUrl) {
                    return;
                }
                
                try {
                    const message = JSON.parse( event.data );
                    if ( typeof message === 'object' && message.event === 'phoenix:authenticationCompleted' ) {
                        resolve( message.data.success ? message.data.token : false );
                    }
                } catch (error) {
                    console.log('proxyAuthenticate: Failed to parse message:', error);
                }
            };
        },
        init() {
            if ( document.readyState === 'loading' ) {  // Loading hasn't finished yet
                document.addEventListener( 'DOMContentLoaded', () => this.embedHiddenSSIFrame());
            } else {  // `DOMContentLoaded` has already fired
                this.embedHiddenSSIFrame();
            }
        },
        async logout(): Promise<void> {
            this.init();
            await new Promise<void>((resolve, reject) => {
                const timeout = setTimeout(() => reject(new Error('Sign-out service did not load')), config.authWaitTime);
                whenLoaded.then(() => { clearTimeout(timeout); resolve(); });
            });
            // Do not call authenticate as a sign-out probe: it can refresh
            // credentials or silently sign in through an external provider.
            // The current bridge has no correlated logout acknowledgment.
            // An absent signed-out reply remains an unconfirmed outcome.
            await new Promise<void>((resolve, reject) => {
                const frame = document.getElementById('ssIFrame_gravity') as HTMLIFrameElement | null;
                const origin = new URL(config.siteUrl).origin;
                const finish = (error?: Error) => {
                    clearTimeout(timeout);
                    window.removeEventListener('message', listener);
                    if (error) reject(error); else resolve();
                };
                const listener = (event: MessageEvent) => {
                    if (event.origin !== origin || event.source !== frame?.contentWindow) return;
                    try {
                        const message = JSON.parse(event.data);
                        if (message?.event !== 'phoenix:authenticationCompleted') return;
                        if (message.data?.success === false) finish();
                        // An earlier in-flight authentication may still succeed.
                        // Only an explicit signed-out response completes logout.
                    } catch { /* Ignore unrelated messages. */ }
                };
                const timeout = setTimeout(() => finish(new Error('Sign-out could not be confirmed')), config.authWaitTime);
                window.addEventListener('message', listener);
                this.sendMessageProxy('gravity::logout');
            });
        }
    };
}
